import { Component, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { JsaPaperService } from '../../../../services/jsaPaper/jsaPaper.service';
import { Router } from '@angular/router';
import { SubmitDiagDirective } from '../../../../services/diagnostics/submit-diag.directive';

@Component({
    selector: 'app-jsa-paper',
    imports: [
    SubmitDiagDirective,
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatDatepickerModule
],
    templateUrl: './jsa-paper.component.html',
    styleUrl: './jsa-paper.component.css'
})
export class JsaPaperComponent implements OnInit {
  isSubmitting = false;
  warningMessage = '';

  form: FormGroup;
  selectedFile: File | null = null;

  // sessionStorage draft key — see saveDraft/restoreDraft below. Note: the uploaded file
  // cannot be serialized, so only the text fields are preserved; the file must be re-attached.
  private readonly draftKey = 'jsapaper-draft';

  constructor(private fb: FormBuilder, private jsaPaperService: JsaPaperService, private router: Router, private cdr: ChangeDetectorRef, private destroyRef: DestroyRef) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      email: [null, Validators.required],
      date: [null, Validators.required],
      office: [null, Validators.required],
      name: [null, Validators.required],
      file: [null, Validators.required]
    });

    // Restore a draft left behind by a prior submit that was blocked for re-auth.
    this.restoreDraft();
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  create(): void {
    if (this.isSubmitting) return;

    if (this.form.invalid || !this.selectedFile) {
      return;
    }

    this.isSubmitting = true;
    this.warningMessage = '';

    // Persist BEFORE the request so the text fields survive an auth-recovery redirect/refresh
    // (the file cannot be serialized and must be re-attached).
    this.saveDraft();

    const formData = new FormData();
    formData.append('email', this.form.controls['email'].value);
    formData.append('date', this.form.controls['date'].value);
    formData.append('office', this.form.controls['office'].value);
    formData.append('name', this.form.controls['name'].value);
    formData.append('file', this.selectedFile);

    this.jsaPaperService.createJsaPaper(formData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearDraft();
          this.router.navigate(['success']);
          this.isSubmitting = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error, submission failed', err);
          this.isSubmitting = false;
          this.warningMessage = this.submissionErrorMessage(err);
          this.cdr.detectChanges();
        },
      });
  }

  private submissionErrorMessage(err: HttpErrorResponse): string {
    // The file is not preserved across a refresh, so guidance is explicit about re-attaching.
    const preserved = 'Your text entries have been preserved, but you will need to re-attach your file.';
    if (err?.status === 401 || err?.status === 403) {
      return `Your session has expired or you've been signed out. Please refresh the page to sign back in, then re-attach your file and resubmit. ${preserved}`;
    }
    if (err?.status === 0) {
      return `Submission failed — we couldn't reach the server. Please check your connection and try again. ${preserved}`;
    }
    return `Submission failed. Please try again in a moment. ${preserved}`;
  }

  // --- Draft persistence (sessionStorage) --------------------------------------------
  // Best-effort; text fields only (the File object cannot be serialized). Never let storage
  // access break a submit or a page load.

  private saveDraft(): void {
    try {
      const { file, ...rest } = this.form.getRawValue();
      sessionStorage.setItem(this.draftKey, JSON.stringify(rest));
    } catch {
      // storage unavailable — submit proceeds normally.
    }
  }

  private clearDraft(): void {
    try {
      sessionStorage.removeItem(this.draftKey);
    } catch {
      // ignore
    }
  }

  private restoreDraft(): void {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(this.draftKey);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const draft = JSON.parse(raw);
      // Date control serializes to an ISO string; revive it for the Material datepicker.
      if (draft.date) draft.date = new Date(draft.date);
      this.form.patchValue(draft);
      this.warningMessage =
        'We restored your unsaved JSA paper entries from a previous attempt. Please re-attach your file, review, then resubmit.';
      this.cdr.detectChanges();
    } catch {
      this.clearDraft();
    }
  }
}
